import { useTranslations } from '@vocab/react';
import { Stack, Text } from 'braid-design-system';
import React, {
  useRef,
  forwardRef,
  type Ref,
  type RefObject,
  useImperativeHandle,
} from 'react';

import {
  BENEFITS,
  QUALIFICATIONS,
  RESPONSIBILITIES,
} from 'src/app/shared/constants/formDataFields';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { TextInputList } from 'src/app/v1/views/components/TextInputList';
import { HintText } from 'src/app/v1/views/shared/HintText';
import type { GuidedWritingFlowRef } from 'src/types/formFields';

import translations from './.vocab';
import {
  benefitsOnBlurNewTrackingEvent,
  benefitsOnRemovedNewTrackingEvent,
  qualificationsOnRemovedNewTrackingEvent,
  qualificiationsOnBlurNewTrackingEvent,
  responsibilitiesOnRemovedNewTrackingEvent,
  responsiblitiesOnBlurNewTrackingEvent,
} from './analytics/newTrackingEvents';

export const GuidedWritingFlow = forwardRef(
  (props: any, ref: Ref<GuidedWritingFlowRef>) => {
    const benefitsRef: RefObject<any> = useRef();
    const experienceRef: RefObject<any> = useRef();
    const responsibilitiesRef: RefObject<any> = useRef();

    useImperativeHandle(ref, () => ({ handleValidation }));
    const { t } = useTranslations(translations);

    const handleValidation = (skipIfNull = false): string[] | null => {
      const errors = [
        benefitsRef.current?.handleValidation(skipIfNull),
        experienceRef.current?.handleValidation(skipIfNull),
        responsibilitiesRef.current?.handleValidation(skipIfNull),
      ].filter((field) => Boolean(field));

      return errors.length > 0 ? errors : null;
    };

    return (
      <Stack space="medium">
        <Stack space="small">
          <Text weight="strong">{t('guided-writing-flow-tasks-heading')}</Text>
          <Text tone="secondary">
            {t('guided-writing-flow-tasks-sub-heading')}
          </Text>

          <TextInputList
            id={RESPONSIBILITIES}
            buttonLabel={t('guided-writing-flow-tasks-button')}
            errorMessage={t('guided-writing-flow-tasks-error-message')}
            maxItemLimit={10}
            ref={responsibilitiesRef}
            onBlurTrackingEvent={() => {
              getTrackingAdapter.sendLinkEvent(
                responsiblitiesOnBlurNewTrackingEvent,
              );
            }}
            onRemoveItemTrackingEvent={() => {
              getTrackingAdapter.sendLinkEvent(
                responsibilitiesOnRemovedNewTrackingEvent,
              );
            }}
          />
        </Stack>

        <Stack space="small">
          <Text weight="strong">
            {t('guided-writing-flow-qualifications-heading')}
          </Text>
          <Text tone="secondary">
            {t('guided-writing-flow-qualifications-sub-heading-skills')}
            <br />
            {t('guided-writing-flow-qualifications-sub-heading-experience')}
          </Text>
          <TextInputList
            buttonLabel={t('guided-writing-flow-qualifications-button')}
            errorMessage={t('guided-writing-flow-qualifications-error-message')}
            id={QUALIFICATIONS}
            maxItemLimit={10}
            ref={experienceRef}
            onBlurTrackingEvent={() => {
              getTrackingAdapter.sendLinkEvent(
                qualificiationsOnBlurNewTrackingEvent,
              );
            }}
            onRemoveItemTrackingEvent={() => {
              getTrackingAdapter.sendLinkEvent(
                qualificationsOnRemovedNewTrackingEvent,
              );
            }}
          />
        </Stack>

        <Stack space="small">
          <Text weight="strong">
            {t('guided-writing-flow-benefits-heading', {
              HintText: (children) => (
                <HintText isHeading={false} text={`(${children})`} />
              ),
            })}
          </Text>
          <Text tone="secondary">
            {t('guided-writing-flow-benefits-sub-heading')}
          </Text>

          <TextInputList
            buttonLabel={t('guided-writing-flow-benefits-button')}
            id={BENEFITS}
            maxItemLimit={10}
            ref={benefitsRef}
            onBlurTrackingEvent={() => {
              getTrackingAdapter.sendLinkEvent(benefitsOnBlurNewTrackingEvent);
            }}
            onRemoveItemTrackingEvent={() => {
              getTrackingAdapter.sendLinkEvent(
                benefitsOnRemovedNewTrackingEvent,
              );
            }}
          />
        </Stack>
      </Stack>
    );
  },
);
